import { KeyboardEvent, memo } from 'react';
import { SortDirection } from '@components/Table/model/table';
import ArrowIcon from '@icons/ArrowIcon/ArrowIcon';
import styles from './TableHeaderCell.module.scss';

interface ITableHeaderCellProps {
  name: string;
  colKey: string;
  onSortClick?: (key: string) => void;
  active?: boolean;
  sortable?: boolean;
  sortDirection?: SortDirection;
  width?: string;
}

/**
 * @param direction - Sort icon direction up | down.
 * @param isActive - Indicates whether header cell is active
 * @param sortDirection - Sort direction of the table ascending | descending
 * @returns A React component that displays table header cell.
 */

const renderSortIcon = (
  direction: 'up' | 'down',
  isActive: boolean,
  sortDirection?: SortDirection
) => {
  const isAscending =
    direction === 'up' && sortDirection === SortDirection.Ascending;
  const isDescending =
    direction === 'down' && sortDirection === SortDirection.Descending;
  const shouldHighlight = isActive && (isAscending || isDescending);

  return <ArrowIcon stroke={shouldHighlight ? '#667085' : undefined} />;
};

/**
 * @param props - The props for the component.
 * @param name - Column cell name.
 * @param colKey - Column cell key
 * @param onSortClick - Function which is called after clicking on sort
 * @param active - Indicates whether header cell is active
 * @param sortable - Indicates whether header cell is sortable
 * @param sortDirection - Sort direction of the table ascending | descending
 * @param width - Indicates column cell custom width
 * @returns A React component that displays the table header.
 */

function TableHeaderCellComponent(props: ITableHeaderCellProps) {
  const {
    width,
    onSortClick,
    name,
    colKey,
    active,
    sortDirection,
    sortable = false,
  } = props;

  const onClickHandler = () => {
    if (onSortClick) {
      onSortClick(colKey);
    }
  };

  const interactiveProps = onSortClick
    ? {
        onClick: onClickHandler,
        onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClickHandler();
          }
        },
        role: 'button' as const,
        tabIndex: 0,
      }
    : {};

  return (
    <div
      className={[
        styles.tableCol,
        sortable && styles.sortable,
        active && styles.active,
      ].join(' ')}
      style={{ minWidth: width, maxWidth: width }}
      {...interactiveProps}
    >
      <p>{name}</p>
      {sortable && (
        <div className={styles.sortIcon}>
          <div className={styles.up}>
            {sortable && renderSortIcon('up', !!active, sortDirection)}
          </div>
          <div className={styles.down}>
            {sortable && renderSortIcon('down', !!active, sortDirection)}
          </div>
        </div>
      )}
    </div>
  );
}

const TableHeaderCell = memo(TableHeaderCellComponent);

export default TableHeaderCell;
